import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconDelete, IconEdit, IconPassword } from '../../../../shared/icon/icon'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { formatCount } from '../../../../shared/sso-workspace/format'
import { agoText, utcText } from '../../../../shared/date/local-time'
import { useRoleSysApiKey } from '../../../../role/hook/role-sys'
import { isLive, keyStatus, prefixText, reasonProblem, statusText } from '../../org/api-keys/key-words'
import { statusCell } from '../../org/api-keys/page/api-key-list'
import { listSysKeys, revokeAnyKey, setRateLimit } from './sys-api'
import { cursorPager } from '../../../../shared/table-builder/cursor-pager'
import { PLATFORM_LABEL } from '../../org/api-keys/platform-organization'
import { useNewPlatformKey } from './new-platform-key'
import '../../org/api-keys/api-keys.scss'

// Every organization's API keys (Task 16): filters for the organization,
// status and last use, paged by content's cursor; revoke any key (with a
// reason) and change a key's rate limit. The 30-day requests come from the
// usage rollup.
//
// The platform organization's keys are platform keys (owner 2026-10-23): their
// organization cell reads Platform, and the organization filter offers
// "Platform keys", once the page knows which organization that is
// (platform-organization.js: the session's own, when it is the platform
// organization). The list waits for that answer, so it is read once and never
// shows the organization's name first. Any other organization's name, and its
// "Access off", lead to its page's Partner API access card, where staff switch
// it. (A key's "System key" column is Phase E's id space, another thing.)
//
// The house list page (CLAUDE.md "List page layout"; owner 2026-10-29): the
// filters are one compact toolbar row, and the Keys card's menu has the list's
// add action, New platform key (new-platform-key.js), pinned as its button,
// and Table settings in the ⋮. The card stays whatever the list's state, so
// that button is never lost when a read fails, nor -- outside the card -- the
// dialog showing a new key's secret once: only the grid gives way to the
// notice.
//
// The columns fit a 1280 px window beside the sidebar (owner 2026-10-29: the
// grid scrolled sideways under the action column; measured on dev in a 1280 px
// frame, 799 px for the columns beside the action column). Every column is
// sized to its content (CLAUDE.md "Column widths", with AG Grid's 16 px cell
// padding each side), so the rows are two lines that pair what belongs
// together: the key's name over its prefix, role and system key (Phase E
// ruling E-4, when it has one), its status over its last use, its 30-day
// requests over its rate limit; and the organization and key names are
// ORGANIZATION_MAX and KEY_MAX wide at most (longer ones ellipsize, and spare
// width widens them). The Role, System key, Last used and Rate limit columns
// stay, hidden, in Table Settings (the list sorts nothing itself: the server
// pages it).
export const ORGANIZATION_MAX = 200
export const KEY_MAX = 240

// The organization filter's "Platform keys" choice (?organization=platform).
export const PLATFORM_FILTER = 'platform'

// Where an organization's partner API access is switched: its page's card.
export const accessHref = organizationID => `/admin/organization/list/edit/${organizationID}#api-access`

export const MIN_RATE = 1
export const MAX_RATE = 100000

export const STATUS_FILTERS = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'expiring', label: 'Expires soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' }
]
export const LAST_USED_FILTERS = [
  { value: '', label: 'Any time' },
  { value: 'never', label: 'Never used' },
  { value: 'within_7d', label: 'Used in the last 7 days' },
  { value: 'within_30d', label: 'Used in the last 30 days' },
  { value: 'not_30d', label: 'Not used in 30 days' },
  { value: 'not_90d', label: 'Not used in 90 days' }
]

export const sysRow = (r, platformID) => {
  const status = keyStatus(r)
  const organization = r.organization || {}
  const platform = !!platformID && Number(organization.id) === Number(platformID)
  return {
    ...r,
    status,
    platform,
    // a platform key's organization reads Platform (sorted, exported and searched so)
    organizationName: platform ? PLATFORM_LABEL : organization.name || '',
    platformName: platform ? organization.name || '' : '',
    organizationID: organization.id,
    accessOff: organization.api_access === false,
    keyName: r.name,
    prefixText: prefixText(r.prefix),
    roleName: (r.role && r.role.name) || '',
    statusText: statusText(status),
    lastUsedText: r.last_used_at ? agoText(r.last_used_at) : 'Never used',
    requestsText: formatCount(r.requests_30d),
    rateLimitText: `${formatCount(r.rate_limit_per_minute)} / min`,
    // Phase E (ruling E-4): the id space its partner API calls write
    systemKeyText: r.system_key || ''
  }
}

// GET /api-key-sys pages by cursor (`next`), the grid by row index: the
// shared cursor pager (table-builder/cursor-pager) remembers the cursor that
// reaches each block; content allows up to 500 a page, the grid's blocks are
// 200.
export const sysKeysPager = (filters, setError, platformID) =>
  cursorPager(listSysKeys, r => sysRow(r, platformID), filters, setError, "The API keys couldn't be loaded.")

const organizationCell = cell => {
  const r = cell.data
  if (!r) return ''
  const offTitle = "This organization's partner API access is off: its keys are refused. Switch it on on the organization's page."
  return (
    <span className="ak-name-cell">
      {r.platform
        ? <span><Pill tone="info" title={`A platform key: EducationXR's own, held by its organization${r.platformName ? ` (${r.platformName})` : ''}`}>{PLATFORM_LABEL}</Pill></span>
        : r.organizationID
          ? <Link className="text-truncate" to={accessHref(r.organizationID)} title={`${r.organizationName}: its organization page, at Partner API access`}>{r.organizationName}</Link>
          : r.organizationName}
      {r.accessOff && (r.organizationID
        ? <span><Link className="text-decoration-none" to={accessHref(r.organizationID)} title={offTitle} data-testid="access-off-link"><Pill tone="bad">Access off</Pill></Link></span>
        : <span><Pill tone="bad" title={offTitle}>Access off</Pill></span>)}
    </span>
  )
}
// The key's name (in full on hover when it ellipsizes), and under it its
// prefix, role and system key (Phase E ruling E-4), when it has one.
const keyCell = cell => (cell.data
  ? <span className="ak-name-cell">
    <span className="fw-semibold text-truncate" title={cell.data.keyName}>{cell.data.keyName}</span>
    <span className="sw-cell-sub text-truncate"><span className="ak-prefix">{cell.data.prefixText}</span>{cell.data.roleName ? ` · ${cell.data.roleName}` : ''}{cell.data.systemKeyText ? ` · system key ${cell.data.systemKeyText}` : ''}</span>
  </span>
  : '')
const lastUsedCell = cell => (cell.data ? (cell.data.last_used_at ? <span title={utcText(cell.data.last_used_at)}>{cell.data.lastUsedText}</span> : <span className="sw-hint">Never used</span>) : '')
const requestsCell = cell => (cell.data
  ? <span className="ak-stack">
    <span title={cell.data.requests_30d === null || cell.data.requests_30d === undefined ? 'Not counted yet' : undefined}>{cell.data.requestsText}</span>
    <span className="sw-cell-sub">{`Limit ${cell.data.rateLimitText}`}</span>
  </span>
  : '')
const rateCell = cell => (cell.data ? cell.data.rateLimitText : '')
const roleCell = cell => (cell.data ? cell.data.roleName : '')
const systemKeyCell = cell => (cell.data && cell.data.systemKeyText ? <span className="sw-mono">{cell.data.systemKeyText}</span> : '')

// A compact filter of the toolbar row, named by its aria-label (no stacked label).
const Select = ({ id, label, value, onChange, options }) =>
  <select id={id} aria-label={label} className="form-select form-select-sm" value={value} onChange={e => onChange(e.target.value)}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>

export const SysKeysTab = ({ platform }) => {
  const ids = { org: useId(), status: useId(), lastUsed: useId(), rate: useId(), reason: useId() }
  const role = useRoleSysApiKey()
  const canUpdate = !!role.accessUpdate
  const canDelete = !!role.accessDelete
  const { search } = useLocation()
  const [organization, setOrganization] = useState(() => new URLSearchParams(search).get('organization') || '')
  const [status, setStatus] = useState('')
  const [lastUsed, setLastUsed] = useState('')
  const [reload, setReload] = useState(0)
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()

  const platformID = (platform && platform.organizationID) || null
  const platformKnown = !platform || platform.status !== 'loading'
  // "Platform keys" stands for the platform organization; with none known (the
  // session is elsewhere) the choice is not offered, and one from the address
  // falls back to every organization. While the answer is awaited the choice is
  // offered, so an address's ?organization=platform names it and is never
  // looked up as an organization.
  useEffect(() => {
    if (organization === PLATFORM_FILTER && platformKnown && !platformID) setOrganization('')
  }, [organization, platformKnown, platformID])
  const organizationID = organization === PLATFORM_FILTER ? platformID || '' : organization
  const filters = useMemo(() => ({ organization_id: organizationID, status, last_used: lastUsed }), [organizationID, status, lastUsed])
  // The read the grid is showing; the System key column shows once a block of
  // it holds a key with one, and a new read (filters, a reload) starts hidden.
  const readKey = `${JSON.stringify(filters)}:${platformID}:${reload}`
  const getRows = useMemo(() => sysKeysPager(filters, setError, platformID), [filters, setError, platformID])
  // the list waits for the platform answer: read once, labelled right
  const waiting = !platformKnown || (organization === PLATFORM_FILTER && !platformID)
  const change = set => value => { setError(null); set(value) }
  // a platform key was made: the list reads its rows again
  const made = useCallback(() => setReload(n => n + 1), [])
  const newKey = useNewPlatformKey({ platform, onCreated: made })

  // the dialogs: a key's rate limit, a revoke
  const [limiting, setLimiting] = useState(null)
  const [limit, setLimit] = useState('')
  const [revoking, setRevoking] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [actError, actFailure, setActError] = useErrorLine()

  const askLimit = useCallback(r => { setActError(null); setLimit(String(r.rate_limit_per_minute || '')); setLimiting(r) }, [setActError])
  const askRevoke = useCallback(r => { setActError(null); setReason(''); setRevoking(r) }, [setActError])

  const act = async (fn, done) => {
    setBusy(true)
    setActError(null)
    try {
      await fn()
      done()
      setReload(n => n + 1)
    } catch (err) {
      setActError(serverMessage(err, "That didn't work."), err)
    }
    setBusy(false)
  }

  const saveLimit = () => {
    if (busy || !limiting) return
    const n = Number(limit)
    if (!Number.isInteger(n) || n < MIN_RATE || n > MAX_RATE) return setActError(`A limit is a whole number of requests a minute, from ${MIN_RATE} to ${formatCount(MAX_RATE)}.`)
    return act(() => setRateLimit(limiting.id, n), () => setLimiting(null))
  }

  const revoke = () => {
    if (busy || !revoking) return
    const problem = reasonProblem(reason)
    if (problem) return setActError(problem)
    return act(() => revokeAnyKey(revoking.id, reason), () => setRevoking(null))
  }

  const menu = useMemo(() => r => (
    <Menu small>
      <MenuItem Icon={IconEdit} text="Rate limit" pin show={canUpdate && isLive(r)} onClick={() => askLimit(r)} />
      <MenuItem Icon={IconDelete} text="Revoke" pin show={canDelete && isLive(r)} onClick={() => askRevoke(r)} />
    </Menu>
  ), [canUpdate, canDelete, askLimit, askRevoke])

  return (
    <>
      <div className="sw-toolbar" role="group" aria-label="Filter the keys">
        <div className="ak-toolbar-org">
          <OrgPicker id={ids.org} label="Organization" value={organization} onChange={change(setOrganization)} allOption={{ value: '', label: 'Every organization' }}
            extraOptions={platformID || !platformKnown ? [{ value: PLATFORM_FILTER, label: 'Platform keys' }] : undefined} small />
        </div>
        <Select id={ids.status} label="Status" value={status} onChange={change(setStatus)} options={STATUS_FILTERS} />
        <Select id={ids.lastUsed} label="Last used" value={lastUsed} onChange={change(setLastUsed)} options={LAST_USED_FILTERS} />
      </div>

      <FleetGrid title="Keys" tableRef={tableRef} rowHeight={ROW_HEIGHT_TWO_LINE}
        menu={newKey.item}>
        {error
          ? <Notice tone="bad" role="alert">{error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={() => { setError(null); setReload(n => n + 1) }}>Try again</button><ErrorReference error={failure} /></Notice>
          : waiting
            ? <p className="sw-hint m-0 py-2">Loading…</p>
            : <TableAgInfinite key={readKey} ref={tableRef} queryKey="" onGetRows={getRows} rowHeight={ROW_HEIGHT_TWO_LINE} hideSearch
              emptyState={{ Icon: IconPassword, title: 'No API keys', message: 'No key matches these filters.' }}>
              <ColumnFormat field="organizationName" header="Organization" sortable={false} flex={1.2} textMax={ORGANIZATION_MAX} stacked render={organizationCell} />
              <ColumnFormat field="keyName" header="Key" sortable={false} flex={1.5} textMax={KEY_MAX} stacked render={keyCell} />
              <ColumnFormat field="roleName" header="Role" sortable={false} render={roleCell} hide />
              <ColumnFormat field="systemKeyText" header="System key" sortable={false} fit="content" render={systemKeyCell} hide />
              <ColumnFormat field="statusText" header="Status" sortable={false} fit="content" stacked render={statusCell} />
              <ColumnFormat field="lastUsedText" header="Last used" sortable={false} fit="content" render={lastUsedCell} hide />
              <ColumnFormat field="requestsText" header="Requests (30 days)" sortable={false} fit="content" stacked render={requestsCell} />
              <ColumnFormat field="rateLimitText" header="Rate limit" sortable={false} fit="content" render={rateCell} hide />
              <ColumnMenu menu={menu} />
            </TableAgInfinite>}
      </FleetGrid>
      {newKey.dialogs}

      <ConfirmDialog
        open={Boolean(limiting)}
        title={limiting ? `Rate limit of "${limiting.name}"` : ''}
        body={`How many requests a minute this key may make, from ${MIN_RATE} to ${formatCount(MAX_RATE)}. Past it, requests get 429 until the minute is over.`}
        confirmText="Save limit"
        confirmClassName="btn sw-btn-primary"
        busy={busy}
        error={actError}
        failure={actFailure}
        onConfirm={saveLimit}
        onCancel={() => setLimiting(null)}
        scrim>
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.rate}>Requests a minute</label>
          <input id={ids.rate} className="form-control" type="number" min={MIN_RATE} max={MAX_RATE} step={1} value={limit} onChange={e => setLimit(e.target.value)} />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(revoking)}
        title={revoking ? (revoking.platform ? `Revoke the platform key "${revoking.name}"?` : `Revoke "${revoking.name}" of ${revoking.organizationName}?`) : ''}
        body="Requests made with this key stop working on the next request. The organization's admins see that EducationXR revoked it. This can't be undone."
        confirmText="Revoke key"
        busy={busy}
        error={actError}
        failure={actFailure}
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}
        scrim>
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.reason}>Reason (optional)</label>
          <textarea id={ids.reason} className="form-control" rows={2} maxLength={600} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
      </ConfirmDialog>
    </>
  )
}
